/**
 * AgentRunStepTimeline —— 步骤时间线。
 *
 * 数据源是「快照为底 + 事件增量」合并后的视图步骤（runView），新步骤由
 * step.started 事件实时追加，无需刷新页面。每步展示：序号 / 类型徽标 /
 * 技能名 / 状态 / 耗时 / tokens / 摘要（默认折叠，点击展开；skill 步摘要
 * 为结构化 JSON 时由 AgentRunFindingsCard 呈现）。
 *
 * 序号直接取 stepIndex：后端 step_index 为 1 基（真实运行三步返回 1/2/3），
 * 前端不做 +1 换算。
 */

import { Tag, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { formatDuration } from "../../utils";
import {
  AGENT_RUN_STEP_STATUS_LABELS,
  type AgentRunStepStatus,
} from "../../types/agentRun";
import type { AgentRunStepView } from "./runView";
import AgentRunFindingsCard from "./AgentRunFindingsCard";

/** 后端 AgentRunStepKind.SKILL（vendor/joint-common enums.py）：技能步摘要
 *  可能是 doc-review 的结构化 JSON，交给 AgentRunFindingsCard 解析呈现。 */
const SKILL_KIND = "skill";

const { Text } = Typography;

const STEP_STATUS_TAG_COLOR: Record<AgentRunStepStatus, string> = {
  running: "processing",
  succeeded: "success",
  failed: "error",
  skipped: "default",
  cancelled: "warning",
};

function StepStatusIcon({ status }: { status: AgentRunStepStatus }) {
  if (status === "running") return <LoadingOutlined style={{ color: "var(--jerry-primary-500)" }} />;
  if (status === "succeeded") return <CheckCircleOutlined style={{ color: "var(--jerry-success-600)" }} />;
  if (status === "failed") return <CloseCircleOutlined style={{ color: "#DC2626" }} />;
  return <MinusCircleOutlined style={{ color: "var(--jerry-text-tertiary)" }} />;
}

function StepStatusTag({ status }: { status: AgentRunStepStatus }) {
  return (
    <Tag color={STEP_STATUS_TAG_COLOR[status] ?? "default"}>
      {AGENT_RUN_STEP_STATUS_LABELS[status] ?? status}
    </Tag>
  );
}

function StepRow({ step }: { step: AgentRunStepView }) {
  return (
    <li
      data-testid={`agent-run-step-${step.stepIndex}`}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--jerry-sp-3)",
        padding: "var(--jerry-sp-3) 0",
        borderBottom: "1px solid var(--jerry-border-default)",
      }}
    >
      <StepStatusIcon status={step.status} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--jerry-sp-2)", flexWrap: "wrap" }}>
          <Text strong style={{ color: "var(--jerry-text-primary)" }}>
            步骤 {step.stepIndex}
          </Text>
          {step.kind && (
            <Tag data-testid={`agent-run-step-${step.stepIndex}-kind`} color="purple">
              {step.kind}
            </Tag>
          )}
          {step.skillName && <Text type="secondary">{step.skillName}</Text>}
          <StepStatusTag status={step.status} />
        </div>
        <div
          data-testid={`agent-run-step-${step.stepIndex}-metrics`}
          style={{
            display: "flex",
            gap: "var(--jerry-sp-4)",
            fontSize: "var(--jerry-font-size-xs)",
            color: "var(--jerry-text-tertiary)",
            marginTop: "var(--jerry-sp-1)",
          }}
        >
          <span>耗时 {step.durationMs != null ? formatDuration(step.durationMs / 1000) : "-"}</span>
          <span>tokens {step.tokensUsed != null ? step.tokensUsed.toLocaleString() : "-"}</span>
        </div>
        {step.summary && (
          <details data-testid={`agent-run-step-${step.stepIndex}-summary`} style={{ marginTop: "var(--jerry-sp-1)" }}>
            <summary style={{ cursor: "pointer", fontSize: "var(--jerry-font-size-xs)", color: "var(--jerry-text-link)" }}>
              摘要
            </summary>
            {step.kind === SKILL_KIND ? (
              <AgentRunFindingsCard summary={step.summary} />
            ) : (
              <Text type="secondary" style={{ whiteSpace: "pre-wrap", fontSize: "var(--jerry-font-size-sm)" }}>
                {step.summary}
              </Text>
            )}
          </details>
        )}
      </div>
    </li>
  );
}

export default function AgentRunStepTimeline({ steps }: { steps: AgentRunStepView[] }) {
  if (steps.length === 0) {
    return (
      <div data-testid="agent-run-steps-empty" style={{ padding: "var(--jerry-sp-4) 0", color: "var(--jerry-text-tertiary)" }}>
        任务尚未开始执行步骤
      </div>
    );
  }
  return (
    <ol data-testid="agent-run-steps" style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {steps.map((step) => (
        <StepRow key={step.stepIndex} step={step} />
      ))}
    </ol>
  );
}

